interface TimelineLoadingStripProps {
  /**
   * Which edge the strip is anchored to. RIGHT hangs off the right
   * edge of the viewport; LEFT is offset past the page sidebar so it
   * sits at the right edge of the sticky labels column (= left edge of
   * the day cells area).
   */
  edge: 'left' | 'right';
  /**
   * Width of the labels column. The left-edge strip is pushed right by
   * this amount so it lines up with the right edge of the labels
   * column instead of disappearing behind the page sidebar.
   */
  labelWidth: number;
  /**
   * Pixel offset from the LEFT edge of the viewport where
   * timeline-root begins. For the LEFT edge, the strip sits at
   * `leftOffset + labelWidth`. For the RIGHT edge, the strip uses
   * `right: 0` and ignores this prop.
   *
   * The component tracks this offset via a ResizeObserver in the
   * parent so it updates when the sidebar collapses (lg breakpoint) or
   * the window resizes.
   */
  leftOffset: number;
  /**
   * Where the strip lives within the scroll content. Kept for API
   * compat — the strip is `position: fixed` so it doesn't use these
   * scroll-content offsets.
   */
  startInScroll: number;
  /**
   * End of the strip in scroll content. Kept for API compat.
   */
  endInScroll: number;
  /** Height of the day-header row. Kept for API compat. */
  headerHeight: number;
  /** Height of a regular user row. Kept for API compat. */
  rowHeight: number;
  /** Total number of rows. Kept for API compat. */
  rowCount: number;
}

/**
 * Spinner rendered at the edge of the timeline while a lazy-load is in
 * progress. Uses the same `animate-spin` ring as `LoadingState` (the
 * page-level "Loading..." indicator) — simpler than the old Toggl-style
 * 3-circle animation.
 *
 * The bar is `position: fixed` so it stays anchored to the viewport
 * regardless of horizontal scroll inside timeline-root. We tried
 * `position: absolute` anchored to timeline-root instead, but Chrome
 * shifts absolute children by `-scrollLeft` when the scroll container
 * has `overflow-x: hidden` (which TimelineGrid sets during a load to
 * block scrolling) — the right strip landed at `viewport.x = -1381`
 * instead of `1255` when the user scrolled to the right edge.
 *
 * The LEFT edge needs an extra offset to clear the page Sidebar (which
 * is `fixed left-0 w-64 z-40`) — without it the strip renders behind
 * the sidebar and is invisible. `leftOffset` is the viewport-x of
 * timeline-root's left edge; we add `labelWidth` so the strip aligns
 * with the right edge of the sticky labels column.
 */
export function TimelineLoadingStrip({
  edge,
  labelWidth,
  leftOffset,
}: TimelineLoadingStripProps) {
  const leftStyle =
    edge === 'right' ? 'auto' : `${leftOffset + labelWidth}px`;
  const rightStyle = edge === 'right' ? '0' : 'auto';

  return (
    <div
      data-testid="timeline-loading-strip"
      data-edge={edge}
      className="fixed z-30 pointer-events-none"
      style={{
        top: '50%',
        transform: 'translateY(-50%)',
        left: leftStyle,
        right: rightStyle,
        width: 50,
        height: 130,
      }}
    >
      <div
        data-testid="timeline-loading-strip-content"
        // Brand primary fill (same as the "Add Task" button). Rounded
        // on the inner edge only so it looks like a tab on the side.
        className="relative flex h-full w-full items-center justify-center"
        style={{
          background: 'hsl(217 91% 60%)',
          borderTopLeftRadius: edge === 'right' ? 8 : 0,
          borderBottomLeftRadius: edge === 'right' ? 8 : 0,
          borderTopRightRadius: edge === 'right' ? 0 : 8,
          borderBottomRightRadius: edge === 'right' ? 0 : 8,
        }}
      >
        {/* Same spinner pattern as LoadingState — white ring on the
            blue bar so it stays readable against the primary fill. */}
        <div
          data-testid="timeline-loading-strip-spinner"
          className="h-7 w-7 animate-spin rounded-full border-[3px] border-white/30 border-t-white"
          aria-hidden
        />
      </div>
    </div>
  );
}
